'use client';

import { createContext, useContext, useMemo, useSyncExternalStore } from 'react';
import {
  WHATSAPP_FALLBACK,
  buildWhatsappHref,
  formatWhatsappDigits,
  formatWhatsappDisplay,
  isAndroid,
  resolveWhatsappHref,
} from '@/lib/whatsapp';

interface WhatsAppContextValue {
  raw: string;
  digits: string;
  display: string;
  href: (prefill?: string) => string;
}

const WhatsAppContext = createContext<WhatsAppContextValue | null>(null);

/** The user agent cannot change for the life of the document, so there is
 *  nothing to subscribe to. */
function subscribeNoop(): () => void {
  return () => {};
}

export function WhatsAppProvider({
  number,
  children,
}: {
  number: string;
  children: React.ReactNode;
}) {
  // The user agent never changes, so this is a read of a static external value
  // rather than state to synchronise: subscribe is a no-op. The server snapshot
  // is always false so the first paint matches the server HTML exactly, which
  // keeps an `intent:` URL out of the static output that iOS and desktop get.
  const android = useSyncExternalStore(subscribeNoop, () => isAndroid(), () => false);

  const value = useMemo<WhatsAppContextValue>(() => {
    const raw = number || WHATSAPP_FALLBACK;
    return {
      raw,
      digits: formatWhatsappDigits(raw),
      display: formatWhatsappDisplay(raw),
      // On Android this resolves to a package-pinned intent: URL so WhatsApp
      // Business is not a candidate handler. Everywhere else it is the plain
      // https wa.me link, exactly as before.
      href: (prefill?: string) =>
        android ? resolveWhatsappHref(raw, prefill, navigator.userAgent) : buildWhatsappHref(raw, prefill),
    };
  }, [number, android]);

  return <WhatsAppContext.Provider value={value}>{children}</WhatsAppContext.Provider>;
}

export function useWhatsApp(): WhatsAppContextValue {
  const ctx = useContext(WhatsAppContext);
  if (!ctx) {
    // Fallback for routes that mount the component outside the provider.
    const raw = WHATSAPP_FALLBACK;
    return {
      raw,
      digits: formatWhatsappDigits(raw),
      display: formatWhatsappDisplay(raw),
      href: (prefill?: string) => buildWhatsappHref(raw, prefill),
    };
  }
  return ctx;
}
